import React, { useEffect, useState, useMemo } from 'react';
import {
  ActionTabs, Banner, v2, Icon,
} from '@fieldnation/platform-components';
import {
  Part, PartUsage, USAGE_CODES, NOTE_PLACEHOLDERS, FieldName, Section,
} from './types.d';
import styles from './styles.scss';
import TrackingField from '../ShipmentForm/components/TrackingField';
import AdditionalPartFields from './AdditionalPartFields';
import Photos from './Photos';
import Accordion from '../ProviderSnapshot/Components/Accordion';
import { UsePartFiles } from './usePartFiles';
import {
  getNewPartExcludedFields,
  getUsageHeader,
  RETURN_INSTRUCTION_OPTIONS_NON_RETURNABLE,
  RETURN_INSTRUCTION_OPTIONS_RETURNABLE,
} from './utils';
import { getCarrierFromTrackingNumber } from '../ShipmentForm/utils';

const {
  Select, Checkbox, TextArea,
} = v2;

const AccordionSection = ({
  label,
  children,
}: { label: string; children: React.ReactNode }): JSX.Element => (
  <div className={styles['accordion-panel']}>
    <Accordion label={label} value="" showBadge={false} expand border="bottom">
      <div className={styles['accordion-panel-body']}>
        {children}
      </div>
    </Accordion>
  </div>
);

interface Props {
  partData: Part;
  usageData: PartUsage;
  returnTrackingRequired?: boolean;
  setReturnTrackingRequired?: React.Dispatch<React.SetStateAction<boolean>>
  // UI-only toggle; lifted to Modal.tsx so it survives a Details/Usage tab remount.
  hasReturnLabel?: string;
  setHasReturnLabel?: React.Dispatch<React.SetStateAction<string>>;
  setUsageData: React.Dispatch<React.SetStateAction<PartUsage>>;
  usageCodes: string[];
  isProvider?: boolean;
  partFiles?: UsePartFiles;
}

const Usage = ({
  partData,
  usageData,
  returnTrackingRequired,
  setReturnTrackingRequired,
  hasReturnLabel,
  setHasReturnLabel,
  setUsageData,
  usageCodes,
  isProvider = false,
  partFiles,
}: Props): JSX.Element => {
  const [showReturnTrackingInput, setShowReturnTrackingInput] = useState(false);
  const [showReturnInstruction, setShowReturnInstruction] = useState(false);
  const [showCustomerRetained, setShowCustomerRetained] = useState(false);
  const [returnShipmentTrackingLink, setReturnShipmentTrackingLink] = useState('');

  const usageSelected = Boolean(usageData?.usageCode);
  const isNotReceived = usageData?.usageCode === USAGE_CODES.NOT_RECEIVED;

  const returnInstructionLabel = useMemo(() => {
    const options = [
      ...RETURN_INSTRUCTION_OPTIONS_RETURNABLE,
      ...RETURN_INSTRUCTION_OPTIONS_NON_RETURNABLE,
    ];
    return options.find((opt) => opt.value === partData?.returnInstruction)?.label || partData?.returnInstruction;
  }, [isProvider, partData?.returnInstruction]);

  const updateAdditionalValue = (
    section: Section,
    field: FieldName,
    value: string,
  ) => {
    setUsageData((prev) => ({
      ...prev,
      additionalPartValues: {
        ...prev.additionalPartValues,
        [section]: {
          ...prev.additionalPartValues?.[section],
          [field]: value,
        },
      },
    }));
  };

  const onUsageCodeSelect = (selectedUsageCode) => {
    if (setHasReturnLabel) {
      setHasReturnLabel('yes');
    }

    const isNewCodeNotReceived = selectedUsageCode === USAGE_CODES.NOT_RECEIVED;

    setUsageData({
      ...usageData,
      usageCode: selectedUsageCode,
      ...(selectedUsageCode !== USAGE_CODES.USED
        ? { customerRetained: '' }
        : {}),
      // "Not received" has no Defective Part section — clear the values it owns.
      ...(isNewCodeNotReceived ? {
        returnTracking: '',
        returnCarrier: '',
        returnCarrierOtherName: '',
      } : {}),
      additionalPartValues: {
        ...usageData.additionalPartValues,
        new: {
          ...usageData.additionalPartValues?.new,
          ...(isNewCodeNotReceived ? { internal_number: '' } : {}),
        },
        defective: {
          ...(isNewCodeNotReceived ? {} : usageData.additionalPartValues?.defective),
          return_label_requested: false,
        },
      },
    });

    if (isNewCodeNotReceived) {
      setReturnShipmentTrackingLink('');
    }
  };

  const onRetainedByCustomerChange = (selectedValues) => {
    const isChecked = selectedValues.includes('1');

    // return tracking is not required if customer retained is checked
    if (isChecked && setReturnTrackingRequired) {
      setReturnTrackingRequired(false);
    }

    setUsageData({
      ...usageData,
      customerRetained: isChecked ? '1' : '0',
    });
  };

  // Get carrier tracking link on first load
  useEffect(() => {
    if (usageData?.returnTracking) {
      const { trackingLink } = getCarrierFromTrackingNumber(usageData.returnTracking);
      setReturnShipmentTrackingLink(trackingLink);
    }
  }, []);

  useEffect(() => {
    // Do not show return instruction when usage code is Not Used, Does Not Work, Damaged In Transit or Wrong Part and returnable is No
    // Or when usage code is Not Received
    if (([
      USAGE_CODES.NOT_USED,
      USAGE_CODES.DOES_NOT_WORK,
      USAGE_CODES.DAMAGED_IN_TRANSIT,
      USAGE_CODES.WRONG_PART,
    ].includes(usageData?.usageCode as USAGE_CODES) && partData?.returnable === 'No') ||
      isNotReceived || !usageData?.usageCode
    ) {
      setShowReturnInstruction(false);
    } else {
      setShowReturnInstruction(true);
    }

    // Do not show return tracking label when usage code is `Used` and returnable is `No`
    // Or when Usage code is `Not received`
    if (
      (usageData?.usageCode === USAGE_CODES.USED && partData?.returnable === 'No') ||
      isNotReceived
    ) {
      setShowReturnTrackingInput(false);
      if (setReturnTrackingRequired) {
        setReturnTrackingRequired(false);
      }
    } else {
      if (usageData?.usageCode) {
        setShowReturnTrackingInput(true);
      }
      if (setReturnTrackingRequired) {
        if (hasReturnLabel === 'no') {
          setReturnTrackingRequired(false);
        } else if (([
          // Return tracking is not required if usage code is `Not used`, `Does not work`, `Damaged in transit`, `Wrong part` or `Not received` and returnable is No
          // Or when usage code is `Not received`
          USAGE_CODES.NOT_USED,
          USAGE_CODES.DOES_NOT_WORK,
          USAGE_CODES.DAMAGED_IN_TRANSIT,
          USAGE_CODES.WRONG_PART,
        ].includes(usageData?.usageCode as USAGE_CODES) && partData?.returnable === 'No') ||
          isNotReceived) {
          setReturnTrackingRequired(false);
        } else if (usageData?.customerRetained !== '1') {
          setReturnTrackingRequired(true);
        }
      }
    }

    // Show customer retained checkbox when usage code is Used and returnable is Yes
    // Not applicable when there's no return shipping label at all
    if (
      usageData?.usageCode === USAGE_CODES.USED &&
      partData?.returnable === 'Yes' &&
      hasReturnLabel !== 'no'
    ) {
      setShowCustomerRetained(true);
    } else {
      setShowCustomerRetained(false);
    }
  }, [usageData, hasReturnLabel]);

  return (
    <>
      <AccordionSection label="New Part">
        <div className={styles['usage-header']}>
          {getUsageHeader(partData.externalId, partData.externalNumber, partData.description)}
        </div>
        <Select
          label="Usage code"
          options={usageCodes.map((code) => ({ label: code, value: code }))}
          onChange={(option) => onUsageCodeSelect(option.value)}
          value={usageData.usageCode}
        />
        <AdditionalPartFields
          fields={usageData?.additionalPartFields}
          values={usageData?.additionalPartValues}
          section="new"
          excludeFields={getNewPartExcludedFields(usageData?.usageCode)}
          onChange={updateAdditionalValue}
          usageCode={usageData?.usageCode}
          showRequiredErrors
        />
      </AccordionSection>

      {usageSelected && !isNotReceived && (
        <AccordionSection label="Defective Part">
          <AdditionalPartFields
            fields={usageData?.additionalPartFields}
            values={usageData?.additionalPartValues}
            section="defective"
            onChange={updateAdditionalValue}
            usageCode={usageData?.usageCode}
            showRequiredErrors
          />

          {(showReturnInstruction || showReturnTrackingInput || showCustomerRetained) && (
            <p className={styles['section-subheader']}>RETURN INFORMATION</p>
          )}

          {/* Return instructions */}
          {showReturnInstruction && (
            <div className={styles['return-instruction']}>
              <Icon name="accessTime" size={24} />
              <span>{returnInstructionLabel}</span>
            </div>
          )}

          {/* Return tracking input */}
          {showReturnTrackingInput && (
            <div className={styles['item-spacing-md']}>
              <p className={styles['return-question']}>
                Do you have the return shipping label?
              </p>
              <ActionTabs
                items={[
                  {
                    label: 'Yes',
                    active: hasReturnLabel !== 'no',
                    onClick: () => {
                      if (setHasReturnLabel) {
                        setHasReturnLabel('yes');
                      }
                      setUsageData({
                        ...usageData,
                        additionalPartValues: {
                          ...usageData.additionalPartValues,
                          defective: {
                            ...usageData.additionalPartValues?.defective,
                            return_label_requested: false,
                          },
                        },
                      });
                    },
                  },
                  {
                    label: 'No',
                    active: hasReturnLabel === 'no',
                    onClick: () => {
                      if (setHasReturnLabel) {
                        setHasReturnLabel('no');
                      }
                      setUsageData({
                        ...usageData,
                        returnCarrier: '',
                        returnTracking: '',
                        returnCarrierOtherName: '',
                        customerRetained: '',
                        additionalPartValues: {
                          ...usageData.additionalPartValues,
                          defective: {
                            ...usageData.additionalPartValues?.defective,
                            return_label_requested: true,
                          },
                        },
                      });
                      setReturnShipmentTrackingLink('');
                    },
                  },
                ]}
              />
              {hasReturnLabel !== 'no' && (
                <div className={styles['item-spacing-md']}>
                  <TrackingField
                    inputLabel="Return tracking number"
                    value={{
                      name: usageData.returnCarrier || '',
                      tracking: usageData.returnTracking ?? '',
                      other_name: usageData.returnCarrierOtherName || '',
                      tracking_link: returnShipmentTrackingLink || '',
                    }}
                    onChange={(carrierInfo) => {
                      setUsageData({
                        ...usageData,
                        returnCarrier: carrierInfo?.name || '',
                        returnTracking: carrierInfo.tracking,
                        returnCarrierOtherName: carrierInfo?.other_name ?? '',
                      });
                      setReturnShipmentTrackingLink(carrierInfo.tracking_link || '');
                    }}
                    disabled={usageData.customerRetained === '1'}
                    optional={!returnTrackingRequired}
                    errors={{ name: false, tracking: false }}
                  />
                </div>
              )}
              {hasReturnLabel === 'no' && (
                <div className={styles['item-spacing-md']}>
                  <Banner
                    type="info"
                    message="The buyer will be notified that you need a return label. Save this task and come back when you have the return information."
                  />
                </div>
              )}
            </div>
          )}

          {/* Customer retained input */}
          {showCustomerRetained && (
            <div className={styles['item-spacing-md']}>
              <Checkbox
                options={[{ label: 'Unable to return - customer kept on site', value: '1' }]}
                onChange={onRetainedByCustomerChange}
                value={usageData.customerRetained === '1' ? ['1'] : []}
              />
            </div>
          )}
        </AccordionSection>
      )}

      {usageSelected && (
        <AccordionSection label="Optional Details">
          {partFiles && (
            <div className={styles['item-spacing-md']}>
              <Photos
                files={partFiles.files}
                loading={partFiles.loading}
                loadError={partFiles.loadError}
                canUpload={isProvider}
                canAddMore={partFiles.canAddMore}
                onUpload={partFiles.uploadFiles}
                onRequestDelete={partFiles.requestDelete}
                onRestore={partFiles.restore}
                getFreshUrl={partFiles.getFreshUrl}
              />
            </div>
          )}
          <div className={styles['item-spacing-md']}>
            <TextArea
              id="notes"
              label="Notes"
              placeholder={NOTE_PLACEHOLDERS[usageData.usageCode]}
              value={usageData.note || ''}
              onChange={(e) => setUsageData({
                ...usageData,
                note: e.target.value,
              })}
              optional
            />
          </div>
        </AccordionSection>
      )}
    </>
  );
};

Usage.defaultProps = {
  returnTrackingRequired: null,
  setReturnTrackingRequired: null,
  hasReturnLabel: 'yes',
  setHasReturnLabel: null,
  isProvider: false,
  partFiles: undefined,
};

export default Usage;
